import { Card, Chip, Grid, Typography } from '@mui/material';
import {
  CommunicationAvatar,
  CustomPagination,
  PageLoader,
  SeenStatus,
  UserPermission,
  colors,
} from 'remote/global';

import { Trans, useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { Alert } from 'utils/data/data-types';
import { ShowOrderDetailsModalLink } from 'views/AnalyticsView';
import {
  useOperatorsAlertsQuery,
  usePostOperatorsAlertsStatusMutation,
} from 'api/queries/operators';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { DefaultTFuncReturn } from 'i18next';
import { Box } from '@mui/system';
import { useState } from 'react';
import useOperatorTimezone from '@/utils/hooks/useOperatorTimezone';
import dayjs from 'dayjs';

interface Props {
  title: string;
  description: string | JSX.Element | DefaultTFuncReturn;
  date: string;
  iconUrl?: string;
  iconName?: string;
  status: SeenStatus;
  onCardClick: () => void;
}

function NotificationCard({
  title,
  description,
  date,
  iconUrl,
  iconName,
  status,
  onCardClick,
}: Props) {
  const { t } = useTranslation();

  return (
    <Grid
      item
      container
      xs={12}
      position='relative'
      sx={{
        borderRadius: '8px',
        padding: '16px',
      }}
    >
      {status === SeenStatus.NEW && (
        <FiberManualRecordIcon
          sx={{
            position: 'absolute',
            color: colors.restYellow,
            fontSize: '12px',
            marginLeft: {
              xs: '6px',
              sm: '32px',
            },
            marginTop: '8px',
          }}
        />
      )}
      <Grid
        item
        container
        xs={12}
        component={Card}
        onClick={onCardClick}
        elevation={3}
        sx={{
          cursor: status === SeenStatus.NEW ? 'pointer' : 'default',
          paddingTop: '12px',
          paddingBottom: '12px',
          paddingLeft: {
            xs: '16px',
            sm: '50px',
          },
          paddingRight: {
            xs: '16px',
            sm: '50px',
          },
        }}
      >
        <Grid item width='32px' justifyContent='center' mr='16px'>
          <CommunicationAvatar iconUrl={iconUrl} name={iconName} />
        </Grid>
        <Grid
          item
          xs
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='flex-end'
        >
          <Box>
            <Typography
              sx={{
                color: colors.gray2,
                fontWeight: 700,
                lineHeight: '20px',
                fontSize: '14px',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                color: colors.gray2,
                fontWeight: 400,
                lineHeight: '20px',
                fontSize: '14px',
              }}
            >
              {description}
            </Typography>
            <Typography
              sx={{
                mt: '8px',
                color: colors.gray3,
                fontWeight: 400,
                lineHeight: '18px',
                fontSize: '12px',
              }}
            >
              {date}
            </Typography>
          </Box>
          <Chip
            label={t(`AlertStatus.${status}`)}
            sx={{
              fontSize: '14px',
              borderRadius: '8px',
              padding: '8px',
              color: status === SeenStatus.NEW ? 'white' : colors.gray3,
              background: status === SeenStatus.NEW ? colors.uiBlue600 : colors.gray12,
              fontWeight: 500,
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function AlertItem(alert: Alert) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const { id, type, bookingNumber, status, iconUrl, iconName, createdAt } = alert;
  const postOperatorsAlertsStatusMutation = usePostOperatorsAlertsStatusMutation(String(id));
  const operatorTimezone = useOperatorTimezone();
  const today = dayjs().tz(operatorTimezone);
  const isToday = dayjs(createdAt).isSame(today, 'day');

  const handleUpdateStatusToSeen = () => {
    if (status !== SeenStatus.SEEN) postOperatorsAlertsStatusMutation.mutateAsync(SeenStatus.SEEN);
  };

  return (
    <NotificationCard
      key={id}
      onCardClick={handleUpdateStatusToSeen}
      title={t(`AlertsView.AlertType.${type}.title`, { bookingNumber })}
      description={
        <Trans
          i18nKey={`AlertsView.AlertType.${type}.subtitle`}
          values={{ bookingNumber }}
          components={[
            [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
              permissions?.ORDER as UserPermission
            ) ? (
              <Typography
                component='span'
                sx={{
                  color: colors.gray9,
                  fontSize: '14px',
                  lineHeight: '16px',
                }}
              >
                #{bookingNumber}
              </Typography>
            ) : (
              <ShowOrderDetailsModalLink
                bookingNumber={bookingNumber}
                rideNumber={1}
                text={`#${bookingNumber}`}
              />
            ),
          ]}
        />
      }
      date={
        isToday
          ? t(`MessagesView.chatbox.todayAt`, {
              time: dayjs(createdAt).tz(operatorTimezone).format('HH:mm'),
            })
          : dayjs(createdAt).tz(operatorTimezone).format('DD.MM HH:mm')
      }
      status={status}
      iconUrl={iconUrl}
      iconName={iconName}
    />
  );
}

export function AlertsList({ showAlertHistory = false }: { showAlertHistory?: boolean }) {
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const {
    data: alerts,
    isSuccess,
    isLoading,
  } = useOperatorsAlertsQuery({
    history: showAlertHistory,
    pageNo: page.toString(),
    pageSize: pageSize.toString(),
  });

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage - 1);
  };

  if (isLoading) {
    return (
      <Grid item xs={12}>
        <PageLoader />
      </Grid>
    );
  }

  if (!isSuccess) return null;

  return (
    <Grid container>
      {alerts?.elements.map((alert) => (
        <AlertItem key={alert.id} {...alert} />
      ))}

      {showAlertHistory && alerts?.pageCount > 1 && (
        <CustomPagination pageNo={page} pageCount={alerts.pageCount} onChange={handlePageChange} />
      )}
    </Grid>
  );
}

export default AlertsList;
